import { Box } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { PracticeLocation } from '../../users/types/rbac';
import { geocodeAddress } from '../../../lib/geocode';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon in bundlers (Vite/Webpack) so pins render
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon as string,
  iconRetinaUrl: markerIcon2x as string,
  shadowUrl: markerShadow as string,
});

const TILE_ZOOM = 15;

export interface LocationTileMapProps {
  location: PracticeLocation;
  /** Square size in pixels (default 120). */
  size?: number;
}

export function LocationTileMap({ location, size = 120 }: LocationTileMapProps): JSX.Element {
  const [position, setPosition] = useState<[number, number] | null>(() => {
    if (location.latitude != null && location.longitude != null) {
      return [location.latitude, location.longitude];
    }
    return null;
  });
  const [MapComponent, setMapComponent] = useState<React.ComponentType<{
    center: [number, number];
    size: number;
  }> | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const geocodeRequestedRef = useRef(false);

  // Sync position when location coords change (e.g. after editing address and saving with new lat/lng)
  useEffect(() => {
    if (location.latitude != null && location.longitude != null) {
      setPosition([location.latitude, location.longitude]);
    } else {
      // No coords: clear position so we re-geocode when address is present
      setPosition(null);
    }
  }, [location.latitude, location.longitude]);

  // Delay map mount until container has layout dimensions (avoids Leaflet clipping when height was 0)
  useEffect(() => {
    if (position == null) return;
    const id = requestAnimationFrame(() => {
      setContainerReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, [position]);

  const { addressLine1, addressLine2, city, state, zipCode } = location;
  const legacyAddress = (location as { address?: string }).address;
  // Geocode when we don't have lat/lng but have address parts
  useEffect(() => {
    if (position != null) return;
    const addr = [addressLine1, addressLine2].filter(Boolean).join(', ') || legacyAddress;
    const hasAddress = [addr, city, state, zipCode].some(
      (s) => s != null && String(s).trim() !== ''
    );
    if (!hasAddress) return;
    geocodeRequestedRef.current = true;
    geocodeAddress({
      address: addr,
      city: city,
      state: state,
      zipCode: zipCode,
    }).then((result) => {
      if (result) setPosition(result);
    });
  }, [addressLine1, addressLine2, legacyAddress, city, state, zipCode, position]);

  // Lazy-load react-leaflet
  useEffect(() => {
    import('react-leaflet').then(({ MapContainer, TileLayer, Marker, useMap }) => {
      const pinIcon = L.icon({
        iconUrl: markerIcon as string,
        iconRetinaUrl: markerIcon2x as string,
        shadowUrl: markerShadow as string,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        shadowSize: [41, 41],
      });

      function MapResize() {
        const map = useMap();
        useEffect(() => {
          if (!map) return;
          const run = () => map.invalidateSize();
          run();
          const t1 = setTimeout(run, 50);
          const t2 = setTimeout(run, 200);
          const t3 = setTimeout(run, 400);
          const container = map.getContainer();
          const resizeObserver = new ResizeObserver(() => {
            map.invalidateSize();
          });
          resizeObserver.observe(container);
          return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            resizeObserver.disconnect();
          };
        }, [map]);
        return null;
      }

      function MapCenterSync({ center: c }: { center: [number, number] }) {
        const map = useMap();
        const lat = c?.[0];
        const lng = c?.[1];
        useEffect(() => {
          if (!map || lat == null || lng == null) return;
          map.setView([lat, lng], TILE_ZOOM);
        }, [map, lat, lng]);
        return null;
      }

      setMapComponent(() => function TileMap({ center, size: s }: { center: [number, number]; size: number }) {
        return (
          <div
            style={{
              width: s,
              height: s,
              minWidth: s,
              minHeight: s,
              position: 'relative',
              zIndex: 0,
              isolation: 'isolate',
            }}
          >
            <MapContainer
              center={center}
              zoom={TILE_ZOOM}
              style={{
                height: '100%',
                width: '100%',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
              }}
              zoomControl={false}
              scrollWheelZoom={false}
              doubleClickZoom={false}
              dragging={false}
              attributionControl={false}
            >
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution=""
              />
              <MapResize />
              <MapCenterSync center={center} />
              <Marker position={center} icon={pinIcon} interactive={false} />
            </MapContainer>
          </div>
        );
      });
    });
  }, []);

  if (position == null) {
    return (
      <Box
        style={{
          width: size,
          height: size,
          borderRadius: 'var(--mantine-radius-md)',
          background: 'var(--mantine-color-gray-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            color: 'var(--mantine-color-gray-5)',
            fontSize: 'var(--mantine-font-size-xs)',
          }}
        >
          …
        </span>
      </Box>
    );
  }

  if (!MapComponent || !containerReady) {
    return (
      <Box
        ref={containerRef}
        style={{
          width: size,
          height: size,
          borderRadius: 'var(--mantine-radius-md)',
          background: 'var(--mantine-color-gray-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: 'var(--mantine-color-gray-5)',
            fontSize: 'var(--mantine-font-size-xs)',
          }}
        >
          {!MapComponent ? 'Map…' : ''}
        </span>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      component="div"
      tabIndex={-1}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: 'var(--mantine-radius-md)',
        overflow: 'hidden',
        flexShrink: 0,
        outline: 'none',
        position: 'relative',
        zIndex: 0,
      }}
    >
      <MapComponent center={position} size={size} />
    </Box>
  );
}
